import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2, Clock3, Loader2, MessageSquareWarning,
  RefreshCw, Search, ShieldX, X, Eye, Briefcase,
  GraduationCap, MapPin, Phone, ShieldCheck,
  CalendarDays, CalendarClock, User2, AlertCircle,
  MoreVertical, History,
} from "lucide-react";
import {
  collection, getDocs, orderBy, query, serverTimestamp,
  updateDoc, doc, where, limit, startAfter,
} from "firebase/firestore";
import { db } from "../../firebase";
import AdminLayout from "../layout/AdminLayout";
import { PROFILE_VERIFICATION_STATUSES } from "../../utils/verification";

/* ─────────────────────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────────────────────── */
const verificationActions = [
  {
    key: "approve", label: "Approve",
    status: PROFILE_VERIFICATION_STATUSES.VERIFIED,
    icon: CheckCircle2,
    buttonClass: "bg-emerald-600 hover:bg-emerald-700 text-white",
    iconBg: "bg-emerald-50 text-emerald-600",
    itemClass: "text-emerald-700 hover:bg-emerald-50",
    tone: "success", notice: "Profile approved and verified.",
  },
  {
    key: "changes", label: "Request Changes",
    status: PROFILE_VERIFICATION_STATUSES.CHANGES_REQUESTED,
    icon: MessageSquareWarning,
    buttonClass: "bg-amber-500 hover:bg-amber-600 text-white",
    iconBg: "bg-amber-50 text-amber-600",
    itemClass: "text-amber-700 hover:bg-amber-50",
    tone: "warning", notice: "Changes requested from the user.",
  },
  {
    key: "reject", label: "Reject",
    status: PROFILE_VERIFICATION_STATUSES.REJECTED,
    icon: ShieldX,
    buttonClass: "bg-rose-600 hover:bg-rose-700 text-white",
    iconBg: "bg-rose-50 text-rose-600",
    itemClass: "text-rose-600 hover:bg-rose-50",
    tone: "danger", notice: "Profile verification rejected.",
  },
];

const quickSuggestions = [
  "Please upload photos", "Please enter full name", "Please add date of birth",
  "Please add profession", "Please add education details", "Please add father's name",
  "Please add mother's name", "Please add address details",
  "Please upload a clear face photo", "Photo is blurry or unclear",
  "Please add about me", "Please verify mobile number",
];

/* ─────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────── */
const fmt = (ts) => {
  if (!ts) return "—";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
const fmtFull = (ts) => {
  if (!ts) return "—";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d)) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
};

/* ─────────────────────────────────────────────────────────────
   PORTAL TOOLTIP — fixed position, closes on scroll/resize
───────────────────────────────────────────────────────────── */
const RemarksTooltip = ({ remarks, reviewedAt }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const lines = (remarks || "").split(/\n/).map((s) => s.trim()).filter(Boolean);

  // close on scroll or resize so it doesn't drift
  useEffect(() => {
    if (!visible) return;
    const hide = () => setVisible(false);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [visible]);

  if (!lines.length) return null;

  const show = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      // fixed positioning — no scrollY/scrollX needed
      setPos({ top: r.top - 8, left: r.left + r.width / 2 });
    }
    setVisible(true);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onMouseEnter={show}
        onMouseLeave={() => setVisible(false)}
        className="flex h-6 w-6 items-center justify-center rounded-lg bg-amber-50 text-amber-500 ring-1 ring-amber-200 transition hover:bg-amber-100"
        tabIndex={-1}
        aria-label="Previous admin remarks"
      >
        <History size={12} />
      </button>

      {visible && createPortal(
        <div
          onMouseEnter={show}
          onMouseLeave={() => setVisible(false)}
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            transform: "translate(-50%, -100%)",
            zIndex: 9999,
            pointerEvents: "auto",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.12 }}
            className="w-64 overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-elevated"
          >
            <div className="flex items-center gap-2 border-b border-amber-100 bg-amber-50 px-3 py-2">
              <MessageSquareWarning size={13} className="shrink-0 text-amber-500" />
              <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700">Previous Admin Request</p>
            </div>
            <ul className="space-y-1 px-3 py-2.5">
              {lines.map((line, i) => (
                <li key={i} className="flex items-start gap-1.5 text-[12px] text-ink-700">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                  {line}
                </li>
              ))}
            </ul>
            {reviewedAt && (
              <div className="border-t border-sand-100 px-3 py-2">
                <p className="text-[10.5px] text-ink-400">Sent {fmtFull(reviewedAt)}</p>
              </div>
            )}
          </motion.div>
        </div>,
        document.body
      )}
    </>
  );
};

/* ─────────────────────────────────────────────────────────────
   PORTAL DROPDOWN MENU — fixed position, closes on scroll/resize
───────────────────────────────────────────────────────────── */
const ActionMenu = ({ profile, onAction, saving }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const openMenu = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setMenuPos({
        top: r.bottom + 6,                        // fixed — no scrollY
        right: window.innerWidth - r.right,        // fixed — no scrollX
      });
    }
    setOpen(true);
  };

  // close on outside click
  const handleOutside = useCallback((e) => {
    if (btnRef.current?.contains(e.target)) return;
    if (menuRef.current?.contains(e.target)) return;
    setOpen(false);
  }, []);

  // close on scroll or resize so menu doesn't drift
  useEffect(() => {
    if (!open) return;
    document.addEventListener("mousedown", handleOutside);
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, handleOutside]);

  return (
    <div className="flex items-center justify-end gap-2">
      {/* Review button */}
      <button
        type="button"
        onClick={() => navigate(`/admin/users/${profile.id}`)}
        className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-ink-600 shadow-soft transition hover:border-brand-600 hover:text-brand-600"
      >
        <Eye size={12} /> Review
      </button>

      {/* Kebab trigger */}
      <button
        ref={btnRef}
        type="button"
        onClick={open ? () => setOpen(false) : openMenu}
        disabled={saving === profile.id}
        className={`flex h-8 w-8 items-center justify-center rounded-xl border shadow-soft transition disabled:opacity-50
          ${open ? "border-brand-300 bg-brand-50 text-brand-600" : "border-sand-300 bg-white text-ink-500 hover:border-brand-600 hover:text-brand-600"}`}
      >
        {saving === profile.id
          ? <Loader2 size={14} className="animate-spin text-brand-600" />
          : <MoreVertical size={15} />
        }
      </button>

      {/* Portal dropdown — fixed position, no drift */}
      {open && createPortal(
        <motion.div
          ref={menuRef}
          initial={{ opacity: 0, scale: 0.94, y: -4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.1 }}
          style={{
            position: "fixed",
            top: menuPos.top,
            right: menuPos.right,
            zIndex: 9999,
          }}
          className="w-52 overflow-hidden rounded-2xl border border-sand-200 bg-white shadow-elevated"
        >
          {verificationActions.map((action, idx) => {
            const Icon = action.icon;
            return (
              <div key={action.key}>
                {idx > 0 && <div className="mx-3 border-t border-sand-100" />}
                <button
                  type="button"
                  onClick={() => { onAction(profile, action); setOpen(false); }}
                  className={`flex w-full items-center gap-3 px-4 py-3 text-[13px] font-semibold transition ${action.itemClass}`}
                >
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${action.iconBg}`}>
                    <Icon size={14} />
                  </span>
                  {action.label}
                </button>
              </div>
            );
          })}
        </motion.div>,
        document.body
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────── */
const Verifications = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [savingId, setSavingId] = useState("");
  const [selectedAction, setSelectedAction] = useState(null);
  const [remarks, setRemarks] = useState("");
  const sentinelRef = useRef(null);
  const PAGE_SIZE = 100;

  const {
    data: profilesData, isLoading: loading,
    isFetchingNextPage: loadingMore, fetchNextPage,
    hasNextPage, refetch, isRefetching,
  } = useInfiniteQuery({
    queryKey: ["adminVerificationsPending"],
    queryFn: async ({ pageParam = null }) => {
      try {
        const q = pageParam
          ? query(collection(db, "profiles"),
              where("profileStatus", "==", PROFILE_VERIFICATION_STATUSES.PENDING),
              orderBy("updatedAt", "desc"), startAfter(pageParam), limit(PAGE_SIZE))
          : query(collection(db, "profiles"),
              where("profileStatus", "==", PROFILE_VERIFICATION_STATUSES.PENDING),
              orderBy("updatedAt", "desc"), limit(PAGE_SIZE));
        const snap = await getDocs(q);
        const docs = snap.docs;
        return { profiles: docs.map((d) => ({ id: d.id, ...d.data() })), nextCursor: docs.length === PAGE_SIZE ? docs[docs.length - 1] : null };
      } catch {
        if (pageParam) return { profiles: [], nextCursor: null };
        const snap = await getDocs(query(collection(db, "profiles"),
          where("profileStatus", "==", PROFILE_VERIFICATION_STATUSES.PENDING), limit(PAGE_SIZE)));
        const docs = snap.docs;
        return { profiles: docs.map((d) => ({ id: d.id, ...d.data() })), nextCursor: docs.length === PAGE_SIZE ? docs[docs.length - 1] : null };
      }
    },
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 300000, cacheTime: 1800000, retry: 1,
    onError: () => toast.error("Could not load verification queue."),
  });

  const profiles = profilesData?.pages.flatMap((p) => p.profiles) || [];

  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage || loading || loadingMore) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) fetchNextPage().catch(() => {}); },
      { rootMargin: "300px 0px" }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasNextPage, loading, loadingMore, fetchNextPage]);

  const pendingProfiles = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return profiles;
    return profiles.filter((p) =>
      [p.fullName, p.kmProfileId, p.mobileNumber, p.presentResidence, p.education, p.profession]
        .filter(Boolean).join(" ").toLowerCase().includes(term)
    );
  }, [profiles, search]);

  const openAction = (profile, action) => { setSelectedAction({ profile, action }); setRemarks(""); };
  const closeAction = () => { if (savingId) return; setSelectedAction(null); setRemarks(""); };

  const confirmAction = async () => {
    if (!selectedAction?.profile || !selectedAction?.action) return;
    const { profile, action } = selectedAction;
    setSavingId(profile.id);
    try {
      await updateDoc(doc(db, "profiles", profile.id), {
        profileStatus: action.status,
        verificationRemarks: remarks.trim(),
        verifiedAt: action.status === PROFILE_VERIFICATION_STATUSES.VERIFIED ? serverTimestamp() : null,
        verifiedBy: action.status === PROFILE_VERIFICATION_STATUSES.VERIFIED ? "admin" : null,
        reviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      queryClient.setQueryData(["adminVerificationsPending"], (old) => {
        if (!old) return old;
        return { ...old, pages: old.pages.map((page) => ({ ...page, profiles: page.profiles.filter((p) => p.id !== profile.id) })) };
      });
      setSelectedAction(null); setRemarks("");
      if (action.tone === "success") toast.success(action.notice);
      else if (action.tone === "danger") toast.error(action.notice);
      else toast(action.notice, { icon: "✏️" });
    } catch (err) {
      toast.error(err.message || "Could not update verification status.");
    } finally { setSavingId(""); }
  };

  return (
    <AdminLayout>

      {/* ══ HEADER ══ */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="section-subtle">Review</p>
          <h1 className="mt-1 font-display text-3xl font-bold text-ink-800">Verifications</h1>
          <p className="mt-1 text-[13.5px] text-ink-400">Approve, request changes, or reject pending profiles</p>
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          <div className="inline-flex items-center gap-2.5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] font-semibold text-amber-700">
            <Clock3 size={15} />
            <span>{loading ? "…" : pendingProfiles.length} pending</span>
          </div>
          <button
            type="button" onClick={() => refetch()} disabled={isRefetching || loading}
            className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-sand-300 bg-white text-ink-500 shadow-soft transition hover:border-brand-600 hover:text-brand-600 disabled:opacity-50"
          >
            <RefreshCw size={15} className={isRefetching ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* ══ SEARCH ══ */}
      <div className="mb-5 flex items-center gap-3 rounded-2xl border border-sand-300 bg-white px-4 py-3 shadow-soft focus-within:border-brand-600 focus-within:ring-4 focus-within:ring-brand-600/10 transition">
        <Search size={16} className="shrink-0 text-ink-400" />
        <input
          className="flex-1 bg-transparent text-[14px] text-ink-800 placeholder:text-ink-300 outline-none"
          placeholder="Search by name, Profile ID, mobile, profession…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button onClick={() => setSearch("")} className="shrink-0 text-ink-400 hover:text-brand-600">
            <X size={14} />
          </button>
        )}
      </div>

      {/* ══ DESKTOP TABLE ══ */}
      <div className="hidden md:block rounded-3xl border border-sand-200 bg-white shadow-soft">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px]">
            <thead>
              <tr className="border-b border-sand-200 bg-gradient-to-r from-sand-50 to-white">
                <th className="px-5 py-4 text-left text-[11px] font-bold uppercase tracking-widest text-ink-400">Profile</th>
                <th className="px-5 py-4 text-left text-[11px] font-bold uppercase tracking-widest text-ink-400">Details</th>
                <th className="px-5 py-4 text-left text-[11px] font-bold uppercase tracking-widest text-ink-400">Location</th>
                <th className="px-5 py-4 text-left text-[11px] font-bold uppercase tracking-widest text-ink-400">
                  <span className="flex items-center gap-1.5"><CalendarDays size={11} />Registered</span>
                </th>
                <th className="px-5 py-4 text-left text-[11px] font-bold uppercase tracking-widest text-ink-400">
                  <span className="flex items-center gap-1.5"><CalendarClock size={11} />Last Submitted</span>
                </th>
                <th className="px-5 py-4 text-right text-[11px] font-bold uppercase tracking-widest text-ink-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sand-100">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="px-5 py-3.5">
                      <div className="skeleton h-11 rounded-xl" />
                    </td>
                  </tr>
                ))
              ) : pendingProfiles.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="flex flex-col items-center gap-3 py-20 text-center">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-500">
                        <ShieldCheck size={26} />
                      </div>
                      <div>
                        <p className="font-display font-bold text-ink-700">All caught up</p>
                        <p className="mt-0.5 text-[13px] text-ink-400">No profiles waiting for verification.</p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                pendingProfiles.map((profile) => {
                  const hasPrevRemarks = !!(profile.verificationRemarks?.trim());
                  return (
                    <tr
                      key={profile.id}
                      className="group transition-colors hover:bg-brand-50/40"
                    >
                      {/* ── Profile ── */}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          {profile.imageUrls?.[0] ? (
                            <img
                              src={profile.imageUrls[0]}
                              alt={profile.fullName}
                              className="h-11 w-11 shrink-0 rounded-xl object-cover object-top ring-2 ring-sand-200 group-hover:ring-brand-200 transition"
                            />
                          ) : (
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sand-100 ring-1 ring-sand-200">
                              <User2 size={18} className="text-ink-300" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="truncate font-semibold text-ink-800 text-[13.5px]">{profile.fullName || "No Name"}</p>
                              {/* previous remarks icon */}
                              {hasPrevRemarks && (
                                <RemarksTooltip
                                  remarks={profile.verificationRemarks}
                                  reviewedAt={profile.reviewedAt}
                                />
                              )}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-1">
                              {profile.kmProfileId && (
                                <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-bold text-brand-600 ring-1 ring-brand-100">
                                  {profile.kmProfileId}
                                </span>
                              )}
                              {profile.gender && (
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${
                                  profile.gender === "Female"
                                    ? "bg-pink-50 text-pink-600 ring-pink-100"
                                    : "bg-sky-50 text-sky-600 ring-sky-100"
                                }`}>{profile.gender}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* ── Details ── */}
                      <td className="px-5 py-4 space-y-0.5">
                        {profile.mobileNumber && (
                          <p className="flex items-center gap-1.5 text-[12px] text-ink-500">
                            <Phone size={11} className="shrink-0 text-ink-400" />{profile.mobileNumber}
                          </p>
                        )}
                        {profile.profession && (
                          <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-700">
                            <Briefcase size={11} className="shrink-0 text-ink-400" />{profile.profession}
                          </p>
                        )}
                        {profile.education && (
                          <p className="flex items-center gap-1.5 text-[12px] text-ink-400">
                            <GraduationCap size={11} className="shrink-0 text-ink-400" />{profile.education}
                          </p>
                        )}
                      </td>

                      {/* ── Location ── */}
                      <td className="px-5 py-4">
                        {(profile.presentResidence || profile.state) ? (
                          <p className="flex items-start gap-1.5 text-[12.5px] text-ink-600">
                            <MapPin size={12} className="mt-0.5 shrink-0 text-ink-400" />
                            <span className="leading-snug">{profile.presentResidence || profile.state}</span>
                          </p>
                        ) : <span className="text-ink-300">—</span>}
                      </td>

                      {/* ── Registered ── */}
                      <td className="px-5 py-4">
                        <p className="text-[12.5px] font-medium text-ink-700">{fmt(profile.createdAt)}</p>
                      </td>

                      {/* ── Last Submitted ── */}
                      <td className="px-5 py-4">
                        <div className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 ring-1 ring-amber-100">
                          <CalendarClock size={11} className="shrink-0 text-amber-500" />
                          <p className="whitespace-nowrap text-[11.5px] font-semibold text-amber-700">{fmtFull(profile.updatedAt)}</p>
                        </div>
                      </td>

                      {/* ── Actions ── */}
                      <td className="px-5 py-4">
                        <ActionMenu profile={profile} onAction={openAction} saving={savingId} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ══ MOBILE CARDS ══ */}
      <div className="grid gap-3 md:hidden">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-28 rounded-3xl" />)
        ) : pendingProfiles.length === 0 ? (
          <div className="card flex flex-col items-center gap-3 px-8 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-500">
              <ShieldCheck size={26} />
            </div>
            <div>
              <p className="font-display font-bold text-ink-700">All caught up</p>
              <p className="text-[13px] text-ink-400">No profiles waiting for verification.</p>
            </div>
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {pendingProfiles.map((profile) => {
              const hasPrevRemarks = !!(profile.verificationRemarks?.trim());
              return (
                <motion.div
                  key={profile.id} layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.15 } }}
                  className="overflow-hidden rounded-3xl border border-sand-200 bg-white shadow-soft"
                >
                  <div className="flex items-start gap-3 p-4">
                    {profile.imageUrls?.[0] ? (
                      <img src={profile.imageUrls[0]} alt={profile.fullName}
                        className="h-14 w-14 shrink-0 rounded-2xl object-cover object-top ring-1 ring-sand-200" />
                    ) : (
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-sand-100 ring-1 ring-sand-200">
                        <User2 size={20} className="text-ink-300" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-semibold text-ink-800">{profile.fullName || "No Name"}</p>
                        {hasPrevRemarks && (
                          <RemarksTooltip remarks={profile.verificationRemarks} reviewedAt={profile.reviewedAt} />
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {profile.kmProfileId && (
                          <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-bold text-brand-600">{profile.kmProfileId}</span>
                        )}
                        {profile.gender && (
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                            profile.gender === "Female" ? "bg-pink-50 text-pink-600" : "bg-sky-50 text-sky-600"
                          }`}>{profile.gender}</span>
                        )}
                      </div>
                      <p className="mt-1 text-[12px] text-ink-400">
                        {[profile.profession, profile.state].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <button type="button" onClick={() => navigate(`/admin/users/${profile.id}`)}
                      className="shrink-0 flex items-center gap-1 rounded-xl border border-sand-200 px-2.5 py-1.5 text-[11px] font-semibold text-ink-600 hover:border-brand-600 hover:text-brand-600 transition">
                      <Eye size={11} /> View
                    </button>
                  </div>

                  {/* bottom bar */}
                  <div className="flex items-center justify-between gap-2 border-t border-sand-100 bg-sand-50 px-4 py-2.5">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-[9.5px] font-bold uppercase tracking-widest text-ink-400">Registered</p>
                        <p className="mt-0.5 text-[11.5px] font-semibold text-ink-700">{fmt(profile.createdAt)}</p>
                      </div>
                      <div>
                        <p className="text-[9.5px] font-bold uppercase tracking-widest text-amber-600">Last Submitted</p>
                        <p className="mt-0.5 text-[11.5px] font-semibold text-amber-700">{fmtFull(profile.updatedAt)}</p>
                      </div>
                    </div>
                    <ActionMenu profile={profile} onAction={openAction} saving={savingId} />
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>

      <div ref={sentinelRef} className="h-4" />
      {loadingMore && (
        <div className="flex items-center justify-center gap-2 py-6 text-[13px] font-semibold text-brand-600">
          <Loader2 size={16} className="animate-spin" /> Loading more…
        </div>
      )}

      {/* ══ ACTION MODAL ══ */}
      <AnimatePresence>
        {selectedAction && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={closeAction}
            className="fixed inset-0 z-[80] flex items-end justify-center bg-ink-900/50 px-4 backdrop-blur-sm sm:items-center"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 32, opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md overflow-hidden rounded-t-[28px] bg-white shadow-elevated sm:rounded-[28px]"
            >
              {/* top colour strip */}
              <div className={`h-1.5 w-full ${
                selectedAction.action.tone === "success" ? "bg-emerald-500" :
                selectedAction.action.tone === "danger"  ? "bg-rose-500"    : "bg-amber-400"
              }`} />

              {/* header */}
              <div className="flex items-center gap-3 border-b border-sand-100 px-5 pt-5 pb-4">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${selectedAction.action.iconBg}`}>
                  <selectedAction.action.icon size={19} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-[17px] font-bold text-ink-800">{selectedAction.action.label}</h3>
                  <p className="truncate text-[12px] text-ink-400">
                    {selectedAction.profile.fullName || "Profile"}
                    {selectedAction.profile.kmProfileId ? ` · ${selectedAction.profile.kmProfileId}` : ""}
                  </p>
                </div>
                <button type="button" onClick={closeAction} disabled={Boolean(savingId)} className="btn-icon h-8 w-8 shrink-0">
                  <X size={15} />
                </button>
              </div>

              {/* body */}
              <div className="space-y-3 px-5 py-4">
                {/* show previous remarks inside modal if re-submission */}
                {selectedAction.action.key !== "approve" && selectedAction.profile.verificationRemarks?.trim() && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                    <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-700">
                      <History size={11} /> Previously Requested
                    </p>
                    {selectedAction.profile.verificationRemarks.split("\n").filter(Boolean).map((line, i) => (
                      <p key={i} className="flex items-start gap-1.5 text-[12px] text-amber-800">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />{line}
                      </p>
                    ))}
                  </div>
                )}

                {selectedAction.action.key !== "approve" && (
                  <div className="flex items-start gap-2 rounded-xl bg-sand-100 p-3 text-[12px] text-ink-500">
                    <AlertCircle size={13} className="mt-0.5 shrink-0 text-ink-400" />
                    This message will be shown to the user.
                  </div>
                )}

                {selectedAction.action.key === "changes" && (
                  <div>
                    <p className="mb-2 text-[10.5px] font-bold uppercase tracking-widest text-ink-400">Quick add</p>
                    <div className="flex flex-wrap gap-1.5">
                      {quickSuggestions.map((s) => {
                        const on = remarks.includes(s);
                        return (
                          <button key={s} type="button"
                            onClick={() => setRemarks((prev) =>
                              on ? prev.split("\n").filter((l) => l.trim() !== s).join("\n").trim()
                                 : prev.trim() ? `${prev.trim()}\n${s}` : s
                            )}
                            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${
                              on ? "border-amber-400 bg-amber-50 text-amber-700"
                                 : "border-sand-200 bg-white text-ink-600 hover:border-amber-400 hover:text-amber-700"
                            }`}
                          >{on ? "✓ " : "+ "}{s}</button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <label className="field-label">
                    {selectedAction.action.key === "approve" ? "Admin note (optional)" : "Remarks"}
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={3}
                    placeholder={
                      selectedAction.action.key === "approve" ? "Internal note for records…" :
                      selectedAction.action.key === "changes" ? "Select above or type custom instructions…" :
                      "Reason for rejection…"
                    }
                    className="input mt-1 resize-none text-[14px]"
                  />
                </div>
              </div>

              {/* footer */}
              <div className="grid grid-cols-2 gap-3 border-t border-sand-100 px-5 py-4">
                <button type="button" onClick={closeAction} disabled={Boolean(savingId)} className="btn-ghost py-2.5 text-[13px]">
                  Cancel
                </button>
                <button type="button" onClick={confirmAction} disabled={Boolean(savingId)}
                  className={`inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-2.5 text-[13px] font-semibold text-white shadow-soft transition active:scale-[0.98] disabled:opacity-60 ${selectedAction.action.buttonClass}`}
                >
                  {savingId && <Loader2 size={14} className="animate-spin" />}
                  Confirm {selectedAction.action.label}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </AdminLayout>
  );
};

export default Verifications;
